import React, { useState, useEffect } from "react";
import { Card, Row, Col, Form, InputGroup, Spinner } from "react-bootstrap";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import axios from "axios";
import { var_api } from "../../../constant";

const Reports = () => {
  const itemsPerPage = 15;

  const [reportData, setReportData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);

  // 🔹 Default date range (1st to 10th of current month)
  const today = new Date();
  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const tenthDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 30);

  const [fromDate, setFromDate] = useState(firstDayOfMonth);
  const [toDate, setToDate] = useState(tenthDayOfMonth);

  const handleGenerateList = async () => {
    if (!fromDate || !toDate) {
      setErrors({ dateRange: "Please select both From and To dates" });
      toast.warning("Please select both From and To dates");
      return;
    }

    if (fromDate > toDate) {
      setErrors({ dateRange: "From date cannot be after To date" });
      toast.warning("From date cannot be after To date");
      return;
    }

    setLoading(true);
    try {
      const formatDate = (date) => {
        const d = new Date(date);
        const day = String(d.getDate()).padStart(2, "0");
        const month = String(d.getMonth() + 1).padStart(2, "0");
        const year = d.getFullYear();
        return `${day}-${month}-${year}`;
      };

      const payload = {
        warehouse_id: 2,
        from_date: formatDate(fromDate),
        to_date: formatDate(toDate),
      };

      const res = await axios.get(
        `${var_api}veemart_instore_pos_billing/get-product-totals`,
        { params: payload }
      );

      if (res.data && Array.isArray(res.data)) {
        let filtered = res.data;
        if (searchQuery.trim()) {
          filtered = filtered.filter((item) =>
            item.product_name?.toLowerCase().includes(searchQuery.trim().toLowerCase())
          );
        }

        setReportData(res.data);
        setFilteredData(filtered);
        setCurrentPage(1);
        setErrors({});
      } else {
        setReportData([]);
        setFilteredData([]);
      }
    } catch (error) {
      toast.error("Error fetching report");
    } finally {
      setLoading(false);
    }
  };

  // 🔹 Fetch data when date range changes automatically
  useEffect(() => {
    handleGenerateList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromDate, toDate]);

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const paginatedData = filteredData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  return (
    <>
      {/* 🔹 Full Page Loader */}
      {loading && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(255, 255, 255, 0.9)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 2000,
          }}
        >
          <div className="text-center">
            <Spinner animation="border" variant="primary" style={{ width: "3rem", height: "3rem" }} />
            <div className="mt-3 fw-bold">Loading Reports...</div>
          </div>
        </div>
      )}

      <Card className="shadow-sm">
        <Card.Body>
          <h4>
            <i className="bi bi-file-earmark-text me-2"></i> Item Report
          </h4>

          <Row className="align-items-end mb-3">
            <Col md={6}>
              <Form.Label className="fw-bold">
                <h5>Select Date Range</h5>
              </Form.Label>
              <InputGroup className="gap-2">
                <DatePicker
                  className="form-control"
                  selected={fromDate}
                  onChange={(date) => setFromDate(date)}
                  selectsStart
                  startDate={fromDate}
                  endDate={toDate}
                  placeholderText="From Date"
                  dateFormat="dd/MM/yyyy"
                />
                <DatePicker
                  className="form-control"
                  selected={toDate}
                  onChange={(date) => setToDate(date)}
                  selectsEnd
                  startDate={fromDate}
                  endDate={toDate}
                  minDate={fromDate}
                  placeholderText="To Date"
                  dateFormat="dd/MM/yyyy"
                />
              </InputGroup>
            </Col>

            <Col md={3} className="ms-auto">
              <Form.Control
                type="text"
                placeholder="Search by Product Name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </Col>
          </Row>

          {errors.dateRange && (
            <div className="text-danger mb-2">{errors.dateRange}</div>
          )}

          {/* Table */}
          <div className="table-responsive">
            <table className="table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Product</th>
                  <th>Total Quantity</th>
                </tr>
              </thead>
              <tbody>
                {paginatedData.length === 0 ? (
                  <tr>
                    <td colSpan="3" className="text-center text-muted py-3">
                      No data found
                    </td>
                  </tr>
                ) : (
                  paginatedData.map((item, idx) => (
                    <tr key={idx}>
                      <td>{(currentPage - 1) * itemsPerPage + idx + 1}</td>
                      <td>{item.product_name || "-"}</td>
                      <td>
                        <span className="badge bg-primary">
                          {item.total_qty ?? 0}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card.Body>
      </Card>

      <ToastContainer />
    </>
  );
};

export default Reports;
